import { useCallback, useMemo, useRef, useState } from "react";
import { useDispatchApp } from "../lib/redux";
import { startColdBootCreativeTemplatePreview } from "../actions/creativeTemplates";
import ModalTemplates from "../components/Modal/ModalTemplates";
import ModalTemplateEditor from "../components/Modal/ModalTemplateEditor";
import ModalTemplateProduce from "../components/Modal/ModalTemplateProduce";
import {
  TEMPLATES_MODAL_TYPE,
  TemplatesModalsContext,
  useTemplatesModals,
} from "./templatesModalsContext";

const TemplatesModalInstance = ({ type, props }) => {
  switch (type) {
    case TEMPLATES_MODAL_TYPE.LIST:
      return <ModalTemplates {...props} />;
    case TEMPLATES_MODAL_TYPE.EDITOR:
      return <ModalTemplateEditor {...props} />;
    case TEMPLATES_MODAL_TYPE.PRODUCE:
      return <ModalTemplateProduce {...props} />;
    default: {
      const exhaustiveCheck = type;
      throw new Error(`Unhandled templates modal type: ${exhaustiveCheck}`);
    }
  }
};

export const TemplatesModalsProvider = ({ children }) => {
  const dispatch = useDispatchApp();
  const nextIdRef = useRef(0);
  const idsByTypeRef = useRef({});
  const [stack, setStack] = useState([]);
  const stackRef = useRef(stack);
  stackRef.current = stack;

  const close = useCallback((id) => {
    setStack((current) => current.filter((entry) => entry.id !== id));
    Object.keys(idsByTypeRef.current).forEach((type) => {
      if (idsByTypeRef.current[type] === id) {
        delete idsByTypeRef.current[type];
      }
    });
  }, []);

  const closeTemplatesModals = useCallback(() => {
    idsByTypeRef.current = {};
    setStack([]);
  }, []);

  const upsert = useCallback(
    (type, props = {}) => {
      const { onCloseModal, ...rest } = props;
      let id = idsByTypeRef.current[type];
      const stack = stackRef.current;
      const isOnTop =
        type === TEMPLATES_MODAL_TYPE.LIST ||
        stack[stack.length - 1]?.id === id;
      // MUI portals keep their original DOM position when React reorders keyed
      // children, so a modal brought to the front must remount to render on top.
      if (!id || !isOnTop) {
        id = `templates-modal-${++nextIdRef.current}`;
        idsByTypeRef.current[type] = id;
      }
      const modalId = id;
      setStack((current) => {
        const without = current.filter((entry) => entry.type !== type);
        const nextEntry = {
          id: modalId,
          type,
          props: {
            ...rest,
            modalOpen: true,
            onCloseModal: (...args) => {
              close(modalId);
              onCloseModal?.(...args);
            },
          },
        };
        if (type === TEMPLATES_MODAL_TYPE.LIST) {
          return [nextEntry, ...without];
        }
        return [...without, nextEntry];
      });
      return modalId;
    },
    [close]
  );

  const openTemplatesList = useCallback(
    (props) => upsert(TEMPLATES_MODAL_TYPE.LIST, props),
    [upsert]
  );
  const openTemplateEditor = useCallback(
    (props = {}) => {
      dispatch(startColdBootCreativeTemplatePreview());
      if (!props.initialPreviewProduceID) {
        return upsert(TEMPLATES_MODAL_TYPE.EDITOR, props);
      }
      return upsert(TEMPLATES_MODAL_TYPE.EDITOR, {
        ...props,
        previewRequestKey: `${props.initialPreviewProduceID}:${Date.now()}`,
      });
    },
    [dispatch, upsert]
  );
  const openTemplateProduce = useCallback(
    (props) => upsert(TEMPLATES_MODAL_TYPE.PRODUCE, props),
    [upsert]
  );

  const value = useMemo(
    () => ({
      openTemplatesList,
      openTemplateEditor,
      openTemplateProduce,
      closeTemplatesModals,
      close,
      stack,
    }),
    [
      close,
      closeTemplatesModals,
      openTemplateEditor,
      openTemplateProduce,
      openTemplatesList,
      stack,
    ]
  );

  return (
    <TemplatesModalsContext.Provider value={value}>
      {children}
    </TemplatesModalsContext.Provider>
  );
};

export const TemplatesModalsStack = () => {
  const { stack } = useTemplatesModals();
  return stack.map((entry) => (
    <TemplatesModalInstance
      key={entry.id}
      type={entry.type}
      props={entry.props}
    />
  ));
};
